import { createSlice } from "@reduxjs/toolkit";
import {
  signin,
  onboardingSignup,
  validateReferral,
  forgotPassword,
  resetPassword,
  checkUserStatus,
} from "../thunks/authThunks";
import {
  getUserProfile,
  updateUserProfile,
  addQuickMessage,
  removeQuickMessage,
} from "../thunks/userThunks";

import { safeJsonParse } from "../../utils/jsonUtils";

// Load authentication state from localStorage (if exists)
const storedAuth = safeJsonParse(localStorage.getItem("auth"), {});

const initialState = {
  isAuthenticated: storedAuth.isAuthenticated || false,
  user: storedAuth.user || null,
  companies: storedAuth.companies || [],
  stripeConnect: storedAuth.stripeConnect || {
    hasStripeConnect: false,
    isVerified: false,
  },
  quickMessages: storedAuth.quickMessages || [],
  isSignInModalOpen: false,
  isOnboarded: false,
  referral: null,
  passwordResetMessage: null,
  intendedRoute: null,
  isSessionExpired: false,
  pendingInvitation: null,
  activeCompany: null,

  // Profile loading states
  profileLoading: false,
  profileError: null,
  quickMessagesLoading: false,
  quickMessagesError: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Set intended route
    setIntendedRoute: (state, action) => {
      state.intendedRoute = action.payload;
    },
    // Clear intended route
    clearIntendedRoute: (state) => {
      state.intendedRoute = null;
    },
    setPendingInvitation: (state, action) => {
      state.pendingInvitation = action.payload;
    },
    clearPendingInvitation: (state) => {
      state.pendingInvitation = null;
    },
    // Logout
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.companies = [];
      state.stripeConnect = {
        hasStripeConnect: false,
        isVerified: false,
      };
      state.quickMessages = [];
      state.isOnboarded = false;
      state.intendedRoute = null;
      state.isSessionExpired = false;
      state.isSignInModalOpen = false;
      state.profileLoading = false;
      state.profileError = null;
      state.quickMessagesLoading = false;
      state.quickMessagesError = null;

      // Clear localStorage
      localStorage.removeItem("token");
      localStorage.removeItem("auth");
    },
    // Set reset credentials
    setResetCredentials: (state, action) => {
      state.resetId = action.payload.id;
      state.resetToken = action.payload.token;
    },
    // Clear reset credentials
    clearResetCredentials: (state) => {
      state.resetId = null;
      state.resetToken = null;
    },
    // Open sign in modal
    openSignInModal: (state) => {
      state.isSignInModalOpen = true;
    },
    // Close sign in modal
    closeSignInModal: (state) => {
      state.isSignInModalOpen = false;
      state.resetId = null;
      state.resetToken = null;
    },
    // Handle session expiration
    handleSessionExpiration: (state) => {
      const currentPath = window.location.pathname;

      state.isAuthenticated = false;
      state.user = null;
      state.companies = [];
      state.quickMessages = [];
      state.isSignInModalOpen = true;
      state.isSessionExpired = true;
      state.intendedRoute = currentPath;

      localStorage.removeItem("token");
      localStorage.removeItem("auth");
    },
    // Clear session expired flag
    clearSessionExpired: (state) => {
      state.isSessionExpired = false;
    },
    // Update user data
    updateUser: (state, action) => {
      if (state.user) {
        state.user = {
          ...state.user,
          ...action.payload,
        };
        saveToLocalStorage(state);
      }
    },
    // Update companies
    updateCompanies: (state, action) => {
      state.companies = action.payload;
      saveToLocalStorage(state);
    },
    // Update stripe connect status
    updateStripeConnect: (state, action) => {
      state.stripeConnect = {
        ...state.stripeConnect,
        ...action.payload,
      };
      saveToLocalStorage(state);
    },
    // Update quick messages
    updateQuickMessages: (state, action) => {
      state.quickMessages = action.payload;
      saveToLocalStorage(state);
    },
    // Add single quick message
    addQuickMessageLocal: (state, action) => {
      state.quickMessages.push(action.payload);
      saveToLocalStorage(state);
    },
    // Remove quick message
    removeQuickMessageLocal: (state, action) => {
      state.quickMessages = state.quickMessages.filter(
        (msg) => msg.id !== action.payload,
      );
      saveToLocalStorage(state);
    },
    // Set active company
    setActiveCompany: (state, action) => {
      state.activeCompany = action.payload;
    },
    // Update subscription summary
    updateSubscriptionSummary: (state, action) => {
      if (state.user) {
        state.user.hasActiveSubscription = true;

        // If payload has summary data, update it
        if (action.payload) {
          state.user.subscriptionSummary = {
            ...state.user.subscriptionSummary,
            ...action.payload,
            hasActiveSubscription: true,
          };
        }

        saveToLocalStorage(state);
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(signin.fulfilled, (state, action) => {
        handleAuthSuccess(state, action.payload.data);
        state.isSignInModalOpen = false;
        state.isSessionExpired = false;
        state.pendingInvitation = null;
      })
      .addCase(checkUserStatus.fulfilled, (state, action) => {
        console.log("User status checked:", action.payload);
        const { status } = action.payload?.data || {};

        if (status === "verified" && state.user) {
          state.user.status = "verified";
          saveToLocalStorage(state);
        }
      })
      // Onboarding Signup
      .addCase(onboardingSignup.pending, (state) => {
        state.isOnboarded = false;
      })
      .addCase(onboardingSignup.fulfilled, (state, action) => {
        console.log("onboardingSignup fulfilled with payload:", action.payload);
        handleAuthSuccess(state, action.payload.data);
        state.isOnboarded = true;
        state.isSignInModalOpen = false;
      })
      .addCase(onboardingSignup.rejected, (state) => {
        state.isOnboarded = false;
      })
      // Validate Referral
      .addCase(validateReferral.fulfilled, (state, action) => {
        state.referral = action.payload;
      })
      .addCase(validateReferral.rejected, (state) => {
        state.referral = null;
      })
      // Forgot Password
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.passwordResetMessage = action.payload?.message || null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.passwordResetMessage = action.payload?.message || "Failed";
      })
      // Reset Password
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.passwordResetMessage = action.payload?.message || null;

        if (action.payload?.data) {
          handleAuthSuccess(state, action.payload.data);
        }

        // Clear reset credentials
        state.resetId = null;
        state.resetToken = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.passwordResetMessage = action.payload?.message || "Failed";
      })
      // Get User Profile
      .addCase(getUserProfile.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.profileLoading = false;

        // Update user data with profile information
        if (state.user && action.payload.data) {
          state.user = {
            ...state.user,
            ...action.payload.data, // Merge profile data
          };
          saveToLocalStorage(state);
        }
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload || "Failed to fetch user profile";
      })
      // Update User Profile
      .addCase(updateUserProfile.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.profileLoading = false;

        // Update user data with updated profile
        if (state.user && action.payload.data) {
          state.user = {
            ...state.user,
            ...action.payload.data,
          };
          saveToLocalStorage(state);
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload || "Failed to update user profile";
      })
      // Add Quick Message
      .addCase(addQuickMessage.pending, (state) => {
        state.quickMessagesLoading = true;
        state.quickMessagesError = null;
      })
      .addCase(addQuickMessage.fulfilled, (state, action) => {
        state.quickMessagesLoading = false;

        // Update quick messages if response contains them
        if (action.payload.data?.quickMessages) {
          state.quickMessages = action.payload.data.quickMessages;

          // Also sync with user object if it exists
          if (state.user) {
            state.user.quickMessages = action.payload.data.quickMessages;
          }
          saveToLocalStorage(state);
        }
      })
      .addCase(addQuickMessage.rejected, (state, action) => {
        state.quickMessagesLoading = false;
        state.quickMessagesError =
          action.payload || "Failed to add quick message";
      })
      // Remove Quick Message
      .addCase(removeQuickMessage.pending, (state) => {
        state.quickMessagesLoading = true;
        state.quickMessagesError = null;
      })
      .addCase(removeQuickMessage.fulfilled, (state, action) => {
        state.quickMessagesLoading = false;

        // Update quick messages if response contains them
        if (action.payload.data?.quickMessages) {
          state.quickMessages = action.payload.data.quickMessages;

          // Also sync with user object if it exists
          if (state.user) {
            state.user.quickMessages = action.payload.data.quickMessages;
          }
          saveToLocalStorage(state);
        }
      })
      .addCase(removeQuickMessage.rejected, (state, action) => {
        state.quickMessagesLoading = false;
        state.quickMessagesError =
          action.payload || "Failed to remove quick message";
      });
  },
});

// Helper function to decode JWT token
const decodeToken = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch (error) {
    console.error("Error decoding token:", error);
    return {};
  }
};

// Save state to localStorage
const saveToLocalStorage = (state) => {
  const authData = {
    isAuthenticated: state.isAuthenticated,
    user: state.user,
    companies: state.companies,
    stripeConnect: state.stripeConnect,
    quickMessages: state.quickMessages,
  };

  localStorage.setItem("auth", JSON.stringify(authData));
};

// Handle authentication success
const handleAuthSuccess = (state, data) => {
  console.log("handleAuthSuccess called with data:", data);
  const {
    token,
    userData,
    companies,
    pendingInvitation,
    subscriptionSummary,
    quickMessages,
  } = data;

  const user = userData || data.user;

  if (!user) {
    console.error("No user data found in auth response:", data);
    return;
  }

  const userId = user.id || user._id;

  // Create user object
  const userObj = {
    _id: userId,
    id: userId,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    accountType: user.accountType,
    referralCode: user.referralCode,
    profilePicture: user.profilePicture || null,
    isDefaultPassword: user.isDefaultPassword || false,
    status: user.status || "verified", // Default to verified
    currency: user.currency || "USD",
    enabledManualPayments: user.enabledManualPayments || false,
    // subscription info
    hasActiveSubscription: subscriptionSummary?.hasActiveSubscription ?? false,
    subscriptionSummary: subscriptionSummary ?? null,
  };

  state.isAuthenticated = true;
  state.user = userObj;
  state.companies = companies || [];
  state.isSignInModalOpen = false;
  state.resetId = null;
  state.resetToken = null;
  state.isSessionExpired = false;
  state.pendingInvitation = pendingInvitation || null; // Store pending invitation
  state.quickMessages = quickMessages || [];

  // Initialize stripeConnect and quickMessages if not already set
  if (!state.stripeConnect) {
    state.stripeConnect = {
      hasStripeConnect: false,
      isVerified: false,
    };
  }

  if (!state.quickMessages) {
    state.quickMessages = [];
  }

  // Save to localStorage
  saveToLocalStorage(state);
  localStorage.setItem("token", token);
};

export const {
  logout,
  setResetCredentials,
  clearResetCredentials,
  openSignInModal,
  closeSignInModal,
  handleSessionExpiration,
  setIntendedRoute,
  clearIntendedRoute,
  clearSessionExpired,
  updateUser,
  updateCompanies,
  updateStripeConnect,
  updateQuickMessages,
  addQuickMessageLocal,
  removeQuickMessageLocal,
  setPendingInvitation,
  clearPendingInvitation,
  setActiveCompany,
  updateSubscriptionSummary,
} = authSlice.actions;

export default authSlice.reducer;
